import React, { useState, useEffect, useCallback } from 'react';

/**
 * Upload an edited RTF export and repopulate a report's text blocks.
 *
 * Two-step, preview-then-confirm: on file select we call `parseFn(file)` for a
 * dry-run preview (which blocks change), then `applyFn(file)` on confirm to save.
 * Both callbacks return the axios-style response's `.data`. Drummond-only; the
 * caller is responsible for only rendering this for authorised users.
 */
const STATUS_LABEL = {
  change: 'Will update',
  cleared: 'Will be cleared',
  unchanged: 'No change',
};
const STATUS_COLOUR = {
  change: '#072040',
  cleared: '#ffc107',
  unchanged: '#6c757d',
};

const RtfUploadModal = ({ isOpen, onClose, title, parseFn, applyFn, onApplied }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFile(null); setPreview(null); setParsing(false);
      setApplying(false); setError(null); setDone(null);
    }
  }, [isOpen]);

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    setFile(f); setError(null); setPreview(null); setDone(null); setParsing(true);
    try {
      const data = await parseFn(f);
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not read that file. Is it the exported RTF?');
    } finally {
      setParsing(false);
    }
  }, [parseFn]);

  const handleApply = useCallback(async () => {
    if (!file) return;
    setApplying(true); setError(null);
    try {
      const data = await applyFn(file);
      setDone(data);
      if (onApplied) onApplied(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not apply that file.');
    } finally {
      setApplying(false);
    }
  }, [file, applyFn, onApplied]);

  if (!isOpen) return null;

  const busy = parsing || applying;
  const changes = preview?.changes || [];
  const nChanged = preview?.n_changed || 0;

  return (
    <div className="publish-modal-overlay" onClick={() => !busy && onClose()}>
      <div className="publish-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="publish-modal-header">
          <h3>{title || 'Upload edited Word file'}</h3>
          <button className="publish-modal-close" onClick={() => !busy && onClose()}>&times;</button>
        </div>

        <div className="publish-modal-body">
          {!done && (
            <>
              <p className="publish-info">
                Upload the edited <strong>.rtf</strong> you exported from this report. The text in
                each tagged block will replace the matching section. Nothing is saved until you
                confirm, and the previous version is kept in history.
              </p>
              <input
                type="file"
                accept=".rtf,application/rtf,text/rtf"
                disabled={busy}
                onChange={(e) => handleFile(e.target.files?.[0])}
                style={{ margin: '0.5rem 0 0.75rem' }}
              />
            </>
          )}

          {parsing && <p className="publish-info">Reading file…</p>}

          {preview?.target_warning && !done && (
            <div className="error-banner" style={{ background: '#fff8e1', color: '#8a6d00', borderColor: '#ffc107' }}>
              ⚠ {preview.target_warning} Check you have the right report selected before applying.
            </div>
          )}

          {preview && !done && (
            <>
              <p className="publish-info" style={{ marginBottom: '0.35rem' }}>
                <strong>{nChanged}</strong> section{nChanged !== 1 ? 's' : ''} will change
                {changes.length ? ` (of ${changes.length} found in the file).` : '.'}
              </p>
              <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 4 }}>
                {changes.map((c) => (
                  <div key={c.key} style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 10, alignItems: 'baseline' }}>
                    <span style={{ minWidth: 110, fontSize: 12, fontWeight: 600, color: STATUS_COLOUR[c.status] || '#333' }}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                    <span style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{c.label}</div>
                      {c.new_preview && (
                        <div style={{ fontSize: 12, color: '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.new_preview}
                        </div>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              {preview.unknown_anchors?.length > 0 && (
                <p className="publish-info" style={{ marginTop: '0.5rem', fontSize: 12 }}>
                  Ignored unrecognised tags: {preview.unknown_anchors.join(', ')}
                </p>
              )}
            </>
          )}

          {done && (
            <div className="publish-info" style={{ color: '#2e7d32' }}>
              ✓ Updated {done.n_changed} section{done.n_changed !== 1 ? 's' : ''}.
              {done.changed_keys?.length ? ' The report has been refreshed.' : ' No changes were needed.'}
            </div>
          )}

          {error && <div className="error-banner">{error}</div>}
        </div>

        <div className="publish-modal-footer">
          <button className="btn-cancel" onClick={() => onClose()} disabled={busy}>
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button
              className="btn-save-publish"
              onClick={handleApply}
              disabled={busy || !preview || nChanged === 0}
            >
              {applying ? 'Applying…' : `Apply${nChanged ? ` (${nChanged})` : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RtfUploadModal;
